import { Component, Input, ChangeDetectionStrategy, signal, OnChanges, SimpleChanges, OnInit, ChangeDetectorRef, ElementRef, ViewChild, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { TooltipModule } from '../../../../../shared/ui-kit/components/tooltip/tooltip.module';
import { XPlaceholderRowComponent } from '../../../../../shared/x-kit/components/x-placeholder-row/x-placeholder-row.component';
import { XPlaceholderFixedLoaderComponent } from '../../../../../shared/x-kit/components/x-placeholder-row/x-placeholder-fixed-loader/x-placeholder-fixed-loader.component';
import { XPlaceholderFlexibleLoaderComponent } from '../../../../../shared/x-kit/components/x-placeholder-row/x-placeholder-flexible-loader/x-placeholder-flexible-loader.component';

export type WidgetSize = 'compact' | 'medium' | 'full';

export interface MarketValueData {
  value: string;
  valueRaw: number;
  currency: string;
  landArea: string;
  landAreaRaw: number;
  perSqmExact: string;
  perSqmExactRaw: number;
  valueHigh: string;
  valueHighRaw: number;
  valueLow: string;
  valueLowRaw: number;
  perSqmHigh: string;
  perSqmHighRaw: number;
  perSqmLow: string;
  perSqmLowRaw: number;
  perSqmHigh95: string;
  perSqmHigh95Raw: number;
  perSqmLow95: string;
  perSqmLow95Raw: number;
  // volumePercent: number | null;
  volatility: number | null;
  history?: Array<{
    date: string;
    value: number;
    lower: number;
    upper: number;
  }>;
}

interface ChartPoint {
  x: number;
  y: number;
  date: string;
  value: number;
  lower: number;
  upper: number;
}

interface ChartBar {
  x: number;
  y1: number;
  y2: number;
}

@Component({
  selector: 'app-market-value-card',
  standalone: true,
  imports: [CommonModule, TranslateModule, TooltipModule, XPlaceholderRowComponent, XPlaceholderFixedLoaderComponent, XPlaceholderFlexibleLoaderComponent],
  template: `
    <div class="market-value-card" [class]="'size-' + currentSize()">
      <div class="card-header">
        <div class="title-block">
          <div class="title-text">
            <span class="card-title">{{ currentSize() === 'compact' ? ('market_value_widget_title_short' | translate) : ('market_value_widget_title' | translate) }}</span>
            <button class="info-button" type="button" [tooltip]="'market_value_widget_tooltip' | translate" [tooltipPosition]="'top'" [attr.aria-label]="'market_value_widget_aria_about' | translate" *ngIf="currentSize() !== 'compact'">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <circle cx="8" cy="8" r="7" stroke="#9CA3AF" stroke-width="1.5"/>
                <path d="M8 11V8M8 5h.01" stroke="#9CA3AF" stroke-width="1.5" stroke-linecap="round"/>
              </svg>
            </button>
          </div>
        </div>
        <div class="toolbar-menu" #menuContainer>
          <button class="toolbar-button" type="button" (click)="toggleMenu()" [attr.aria-label]="'market_value_widget_size_menu' | translate">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="3" r="1.5" fill="#9CA3AF"/>
              <circle cx="8" cy="8" r="1.5" fill="#9CA3AF"/>
              <circle cx="8" cy="13" r="1.5" fill="#9CA3AF"/>
            </svg>
          </button>
          <div class="size-menu" *ngIf="isMenuOpen()" #sizeMenu>
            <button class="size-option" [class.active]="currentSize() === 'compact'" (click)="setSize('compact')">
              <span class="size-icon">◼</span>
              <span>{{ 'market_value_widget_size_compact' | translate }}</span>
            </button>
            <button class="size-option" [class.active]="currentSize() === 'medium'" (click)="setSize('medium')">
              <span class="size-icon">▬</span>
              <span>{{ 'market_value_widget_size_medium' | translate }}</span>
            </button>
            <button class="size-option" [class.active]="currentSize() === 'full'" (click)="setSize('full')">
              <span class="size-icon">▮</span>
              <span>{{ 'market_value_widget_size_full' | translate }}</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Skeleton Placeholder -->
      <div class="skeleton-placeholder" *ngIf="isLoading || !data">
        <app-x-placeholder-row>
          <app-x-placeholder-fixed-loader [width]="'100px'" [height]="'24px'"></app-x-placeholder-fixed-loader>
          <app-x-placeholder-flexible-loader [grow]="1"></app-x-placeholder-flexible-loader>
        </app-x-placeholder-row>
        
        <app-x-placeholder-row>
          <app-x-placeholder-fixed-loader [width]="'80px'" [height]="'20px'"></app-x-placeholder-fixed-loader>
          <app-x-placeholder-flexible-loader [grow]="1"></app-x-placeholder-flexible-loader>
        </app-x-placeholder-row>
        
        <app-x-placeholder-row>
          <app-x-placeholder-fixed-loader [width]="'60px'" [height]="'20px'"></app-x-placeholder-fixed-loader>
          <app-x-placeholder-flexible-loader [grow]="1"></app-x-placeholder-flexible-loader>
        </app-x-placeholder-row>
      </div>

      <!-- Content when loaded -->
      <ng-container *ngIf="!isLoading && data">
      <!-- COMPACT SIZE: Main value + list metrics -->
      <div class="value-section compact-layout" *ngIf="currentSize() === 'compact'">
        <div class="main-value-compact">
          <span class="value-number-compact">{{ formatToShort(data.valueRaw) }}</span>
        </div>
        <div class="metrics-list-compact">
          <div class="metric-row">
            <span class="metric-label">{{ 'market_value_widget_exact_val' | translate }}</span>
            <span class="metric-value">{{ data.perSqmExact }}</span>
          </div>
          <div class="metric-row">
            <span class="metric-label">{{ 'market_value_widget_high' | translate }}</span>
            <span class="metric-value">{{ data.perSqmHigh }}</span>
          </div>
          <div class="metric-row">
            <span class="metric-label">{{ 'market_value_widget_low' | translate }}</span>
            <span class="metric-value">{{ data.perSqmLow }}</span>
          </div>
        </div>
      </div>

      <!-- MEDIUM SIZE: Compact value + land area in one row -->
      <div class="value-section medium-layout" *ngIf="currentSize() === 'medium'">
        <div class="value-row-medium">
          <span class="value-number-medium">{{ data.value }}</span>
          <div class="land-area-block">
            <div class="land-label">{{ 'market_value_widget_based_on_land' | translate }}</div>
            <div class="land-value">{{ 'market_value_widget_area' | translate }}: {{ data.landArea }}</div>
          </div>
        </div>
      </div>

      <!-- MEDIUM ONLY: Specific list metrics and Graph -->
      <div class="metrics-list-medium" *ngIf="currentSize() === 'medium'">
        <div class="medium-content-wrapper">
          <div class="metrics-column">
            <div class="metric-row">
              <span class="metric-label">{{ 'market_value_widget_exact_value' | translate }}</span>
              <span class="metric-value">{{ data.perSqmExact }}</span>
            </div>
            <div class="metric-row">
              <span class="metric-label">{{ 'market_value_widget_high' | translate }}</span>
              <span class="metric-value">{{ data.perSqmHigh }}</span>
            </div>
            <div class="metric-row">
              <span class="metric-label">{{ 'market_value_widget_low' | translate }}</span>
              <span class="metric-value">{{ data.perSqmLow }}</span>
            </div>
          </div>

          <!-- Mini Chart for Medium View -->
          <div class="mini-chart-container" *ngIf="chartPaths">
             <svg [attr.viewBox]="chartViewBox" preserveAspectRatio="none">
                <!-- Bars -->
                <line *ngFor="let bar of chartPaths.bars; let last = last"
                      [attr.x1]="bar.x" [attr.y1]="bar.y1"
                      [attr.x2]="bar.x" [attr.y2]="bar.y2"
                      [attr.stroke]="last ? 'rgba(0, 188, 125, 0.50)' : '#E5E7EB'" stroke-width="6" stroke-linecap="round"/>
                <!-- Line -->
                <polyline [attr.points]="chartPaths.line" fill="none" stroke="#9CA3AF" stroke-width="1.5"/>
                <!-- Invisible hit areas -->
                <circle *ngFor="let dot of chartPaths.dots; let i = index"
                        [attr.cx]="dot.x" [attr.cy]="dot.y" r="10"
                        fill="transparent"
                        style="cursor: pointer"
                        (mouseenter)="showTooltip($event, dot)"
                        (mouseleave)="hideTooltip()"/>
                <!-- Visible dots -->
                <circle *ngFor="let dot of chartPaths.dots; let last = last"
                         [attr.cx]="dot.x" [attr.cy]="dot.y" r="5"
                        fill="white" [attr.stroke]="last ? '#10B981' : '#9CA3AF'" [attr.stroke-width]="last ? 2 : 1.5"
                        style="pointer-events: none"/>
             </svg>

            <!-- Tooltip -->
            <div class="chart-tooltip" *ngIf="activeTooltip()"
                 [class.tooltip-left]="tooltipPosition().align === 'left'"
                 [class.tooltip-right]="tooltipPosition().align === 'right'"
                 [class.tooltip-center]="tooltipPosition().align === 'center'"
                 [style.left.px]="tooltipPosition().x"
                 [style.transform]="tooltipPosition().transform"
                 [style.--arrow-left.px]="tooltipPosition().arrowLeft">
              <div class="tooltip-date">{{ activeTooltip()?.date }}</div>
              <div class="tooltip-value">{{ formatNumber(activeTooltip()?.value) }}</div>
              <div class="tooltip-range">{{ formatNumber(activeTooltip()?.lower) }} – {{ formatNumber(activeTooltip()?.upper) }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- FULL SIZE: Main value + land area -->
      <div class="value-section" *ngIf="currentSize() === 'full'">
        <div class="main-value-row">
          <div class="main-value">
            <span class="value-number">{{ data.value }}</span>
            <div class="land-area">
              {{ 'market_value_widget_based_on_land_area' | translate }}:
              <span class="land-area-value">{{ data.landArea }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- FULL ONLY: Value per m² section with compact table -->
      <div class="value-per-section" *ngIf="currentSize() === 'full'">
        <div class="value-per-header">
          <div class="value-per-label">{{ 'market_value_widget_value_per' | translate }}</div>
          <div class="value-unit-chip">m²</div>
        </div>
        <div class="metrics-table">
          <div class="table-column">
            <div class="metric-row">
              <span class="metric-label">{{ 'market_value_widget_exact_value' | translate }}</span>
              <span class="metric-value">{{ data.perSqmExact }}</span>
            </div>
            <div class="metric-row">
              <span class="metric-label">{{ 'market_value_widget_high' | translate }}</span>
              <span class="metric-value">{{ data.perSqmHigh }}</span>
            </div>
            <div class="metric-row">
              <span class="metric-label">{{ 'market_value_widget_low' | translate }}</span>
              <span class="metric-value">{{ data.perSqmLow }}</span>
            </div>
          </div>
          <div class="table-divider"></div>
          <div class="table-column">
            <div class="metric-row">
              <span class="metric-label with-icon">
                {{ 'market_value_widget_volatility' | translate }}
                <button class="info-button tiny" type="button" [tooltip]="'market_value_widget_volatility_tooltip' | translate" [tooltipPosition]="'top'" [attr.aria-label]="'market_value_widget_volatility_aria' | translate">
                  <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                    <circle cx="8" cy="8" r="6" stroke="#9CA3AF" stroke-width="1.2"/>
                    <path d="M8 10.5V8M8 5.5h.01" stroke="#9CA3AF" stroke-width="1.2" stroke-linecap="round"/>
                  </svg>
                </button>
              </span>
              <span class="metric-value">{{ formatVolume(data.volatility) }}</span>
            </div>
            <div class="metric-row">
              <span class="metric-label">{{ 'market_value_widget_95h' | translate }}</span>
              <span class="metric-value">{{ data.perSqmHigh95 }}</span>
            </div>
            <div class="metric-row">
              <span class="metric-label">{{ 'market_value_widget_95l' | translate }}</span>
              <span class="metric-value">{{ data.perSqmLow95 }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Trend Chart Section (Full Size) -->
      <div class="trend-chart-section" *ngIf="currentSize() === 'full' && chartPaths">
         <div class="chart-wrapper">
            <svg [attr.viewBox]="chartViewBox" preserveAspectRatio="none">
               <!-- Bars -->
               <line *ngFor="let bar of chartPaths.bars; let last = last"
                     [attr.x1]="bar.x" [attr.y1]="bar.y1"
                     [attr.x2]="bar.x" [attr.y2]="bar.y2"
                     [attr.stroke]="last ? 'rgba(0, 188, 125, 0.50)' : '#E5E7EB'" stroke-width="8" stroke-linecap="round"/>

               <!-- Line connecting centers -->
               <polyline [attr.points]="chartPaths.line" fill="none" stroke="#9CA3AF" stroke-width="2"/>

               <!-- Invisible hit areas for better hover detection -->
               <circle *ngFor="let dot of chartPaths.dots; let i = index; let last = last"
                       [attr.cx]="dot.x" [attr.cy]="dot.y" r="12"
                       fill="transparent"
                       style="cursor: pointer"
                       (mouseenter)="showTooltip($event, dot)"
                       (mouseleave)="hideTooltip()"/>

               <!-- Visible dots -->
               <circle *ngFor="let dot of chartPaths.dots; let last = last"
                        [attr.cx]="dot.x" [attr.cy]="dot.y" r="6"
                       fill="white" [attr.stroke]="last ? '#10B981' : '#9CA3AF'" [attr.stroke-width]="last ? 2.5 : 2"
                       style="pointer-events: none"/>
            </svg>

            <!-- Tooltip -->
            <div class="chart-tooltip" *ngIf="activeTooltip()"
                 [class.tooltip-left]="tooltipPosition().align === 'left'"
                 [class.tooltip-right]="tooltipPosition().align === 'right'"
                 [class.tooltip-center]="tooltipPosition().align === 'center'"
                 [style.left.px]="tooltipPosition().x"
                 [style.transform]="tooltipPosition().transform"
                 [style.--arrow-left.px]="tooltipPosition().arrowLeft">
              <div class="tooltip-date">{{ activeTooltip()?.date }}</div>
              <div class="tooltip-value">{{ formatNumber(activeTooltip()?.value) }}</div>
              <div class="tooltip-range">{{ formatNumber(activeTooltip()?.lower) }} – {{ formatNumber(activeTooltip()?.upper) }}</div>
            </div>
         </div>
      </div>
      </ng-container>
    </div>
  `,
  styleUrl: './market-value-card.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MarketValueCardComponent implements OnChanges, OnInit, OnDestroy {
  @Input() data: MarketValueData | null = null;
  @Input() isLoading: boolean = false;

  @ViewChild('menuContainer', { static: false }) menuContainer?: ElementRef;

  // Widget size state
  currentSize = signal<WidgetSize>('full');
  isMenuOpen = signal<boolean>(false);
  private clickOutsideHandler?: (event: MouseEvent) => void;

  // Tooltip state
  activeTooltip = signal<ChartPoint | null>(null);
  tooltipPosition = signal<{ x: number; y: number; transform: string; align: 'left' | 'center' | 'right'; arrowLeft: number }>({ x: 0, y: 0, transform: 'translateX(-50%)', align: 'center', arrowLeft: 50 });

  // Chart computed data
  chartPaths: {
    bars: ChartBar[];
    dots: ChartPoint[];
    line: string;
  } | null = null;

  chartViewBox = "0 0 320 120";

  constructor(private cdr: ChangeDetectorRef, private elementRef: ElementRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    console.log('[MarketValueCard] ngOnChanges called', changes);
    if (changes['data'] && this.data?.history) {
      console.log('[MarketValueCard] data.history exists, length:', this.data.history.length);
      this.calculateChartPaths();
    } else {
      console.log('[MarketValueCard] No history in data', this.data);
    }
  }

  ngOnInit() {
    console.log('[MarketValueCard] ngOnInit called, data:', this.data);
    if (this.data?.history) {
      console.log('[MarketValueCard] Calling calculateChartPaths from ngOnInit');
      this.calculateChartPaths();
    } else {
      console.warn('[MarketValueCard] No history data available in ngOnInit');
    }
  }

  ngOnDestroy() {
    this.removeClickOutsideListener();
  }

  private removeClickOutsideListener() {
    if (this.clickOutsideHandler) {
      document.removeEventListener('click', this.clickOutsideHandler, true);
      this.clickOutsideHandler = undefined;
    }
  }

  private addClickOutsideListener() {
    this.removeClickOutsideListener();
    
    this.clickOutsideHandler = (event: MouseEvent) => {
      if (!this.isMenuOpen() || !this.menuContainer) {
        return;
      }

      const target = event.target as Node;
      const menuElement = this.menuContainer.nativeElement;

      // Check if click is outside the menu container
      if (!menuElement.contains(target)) {
        this.isMenuOpen.set(false);
        this.removeClickOutsideListener();
      }
    };

    // Use setTimeout to avoid immediate close on the click that opened the menu
    setTimeout(() => {
      document.addEventListener('click', this.clickOutsideHandler!, true);
    }, 0);
  }

  toggleMenu() {
    this.isMenuOpen.update(v => {
      const newValue = !v;
      if (newValue) {
        // Add listener when menu opens
        this.addClickOutsideListener();
      } else {
        // Remove listener when menu closes
        this.removeClickOutsideListener();
      }
      return newValue;
    });
  }

  setSize(size: WidgetSize) {
    this.currentSize.set(size);
    this.isMenuOpen.set(false);
    this.removeClickOutsideListener();
  }

  formatVolume(value: number | null): string {
    if (value === null || value === undefined || Number.isNaN(value)) {
      return '—';
    }
    return `${new Intl.NumberFormat('id-ID', { maximumFractionDigits: 1 }).format(value * 100)}%`;
  }

  formatToShort(value: number): string {
    const billion = value / 1_000_000_000;
    if (billion >= 1) {
      return `${billion.toFixed(1)}Bn`;
    }
    const million = value / 1_000_000;
    return `${million.toFixed(0)}M`;
  }

  formatNumber(value: number | undefined): string {
    if (value === undefined) return '';
    return new Intl.NumberFormat('id-ID').format(Math.round(value));
  }

  showTooltip(event: MouseEvent, dot: ChartPoint) {
    const target = event.target as SVGCircleElement;
    const svg = target.closest('svg');

    if (!svg) return;

    const svgRect = svg.getBoundingClientRect();
    const container = svg.closest('.chart-wrapper, .mini-chart-container');

    if (!container) return;

    const containerElement = container as HTMLElement;
    const containerRect = containerElement.getBoundingClientRect();

    // Calculate horizontal position relative to container
    const svgWidth = 320; // from viewBox
    const scaleX = svgRect.width / svgWidth;

    // Calculate dot position relative to SVG
    const dotXInSvg = dot.x * scaleX;
    // Calculate SVG position relative to container
    // Use getBoundingClientRect for more accurate positioning
    const svgOffsetX = svgRect.left - containerRect.left;
    // Final position relative to container
    const x = svgOffsetX + dotXInSvg;

    // Get container width for boundary checking
    const containerWidth = (container as HTMLElement).offsetWidth || svgRect.width;

    // Estimate tooltip width (approximate based on content)
    // Tooltip typically has width around 150-200px
    const estimatedTooltipWidth = 180;
    const minOffset = 10; // Minimum distance from container edge

    let finalX = x;
    let transform = 'translateX(-50%)';
    let align: 'left' | 'center' | 'right' = 'center';
    let arrowLeft: number;

    // Check if tooltip would overflow on the left
    if (x - estimatedTooltipWidth / 2 < minOffset) {
      // Align to left edge with padding
      finalX = minOffset;
      transform = 'translateX(0)';
      align = 'left';
      // Calculate arrow position: distance from left edge of tooltip to the dot
      // x is the dot position, finalX is the tooltip left edge position
      arrowLeft = x - finalX;

      // Ensure arrow is not negative (shouldn't happen, but safety check)
      if (arrowLeft < 0) {
        arrowLeft = 0;
      }

      // For left-aligned tooltips, if the dot is very close to the left edge,
      // add a small offset for better visual alignment while still pointing to the dot
      // This prevents the arrow from being too close to the edge visually
      if (arrowLeft < 15) {
        // If arrow would be less than 15px from edge, add a small offset
        // This makes it visually better while still being close to the dot
        arrowLeft = Math.max(arrowLeft, 18);
      }

      // Ensure arrow doesn't go beyond tooltip right edge (with small padding)
      const maxArrowPos = estimatedTooltipWidth - 10;
      if (arrowLeft > maxArrowPos) {
        arrowLeft = maxArrowPos;
      }
    }
    // Check if tooltip would overflow on the right
    else if (x + estimatedTooltipWidth / 2 > containerWidth - minOffset) {
      // Align to right edge with padding
      finalX = containerWidth - minOffset;
      transform = 'translateX(-100%)';
      align = 'right';
      // Calculate arrow position: distance from left edge of tooltip to the dot
      // With left: finalX and translateX(-100%), the transform shifts the element left by 100% width
      // So: left edge position = finalX - estimatedTooltipWidth, right edge = finalX
      // Arrow position from left edge = x - (finalX - estimatedTooltipWidth) = x - finalX + estimatedTooltipWidth
      arrowLeft = x - finalX + estimatedTooltipWidth;
      // Ensure arrow doesn't go beyond tooltip right edge (with small padding)
      const maxArrowPos = estimatedTooltipWidth - 10;
      if (arrowLeft > maxArrowPos) {
        arrowLeft = maxArrowPos;
      }
    }
    else {
      // Centered: arrow points to the dot position
      // finalX is the center of tooltip, so arrow should be at estimatedTooltipWidth / 2
      arrowLeft = estimatedTooltipWidth / 2;
    }

    this.activeTooltip.set(dot);
    this.tooltipPosition.set({ x: finalX, y: 0, transform, align, arrowLeft });
  }

  hideTooltip() {
    this.activeTooltip.set(null);
  }

  private calculateChartPaths() {
    console.log('[MarketValueCard] calculateChartPaths START');
    if (!this.data) {
      console.warn('[MarketValueCard] No data, returning');
      return;
    }
    console.log('[MarketValueCard] data.history:', this.data.history);

    if (!this.data.history || this.data.history.length === 0) {
      console.warn('[MarketValueCard] No history data, returning');
      return;
    }

    const history = this.data.history;
    console.log('[MarketValueCard] Processing', history.length, 'data points');

    const width = 320; // Internal SVG coordinate system width
    const height = 120; // Increased height for taller candles (2x larger)
    const paddingX = 10;
    const paddingYTop = 8; // Top padding
    const paddingYBottom = 8; // Bottom padding for centered positioning

    // Calculate min/max for Y scaling
    const allValues = history.flatMap(h => [h.lower, h.upper]);
    let min = Math.min(...allValues);
    let max = Math.max(...allValues);

    console.log('[MarketValueCard] Y-axis range:', min, 'to', max);

    // Add some breathing room
    const range = max - min;
    min -= range * 0.1;
    max += range * 0.1;

    if (range === 0) {
        min -= 10;
        max += 10;
    }

    const scaleY = (val: number) => {
      // Invert Y because SVG 0 is at top
      // Use different padding for top and bottom to push candles higher
      const availableHeight = height - paddingYTop - paddingYBottom;
      return height - paddingYBottom - ((val - min) / (max - min)) * availableHeight;
    };

    const stepX = history.length > 1
      ? (width - 2 * paddingX) / (history.length - 1)
      : 0;

    const bars: ChartBar[] = [];
    const dots: ChartPoint[] = [];
    const linePoints: string[] = [];

    history.forEach((item, index) => {
      // If single item, center it
      const x = history.length > 1
        ? paddingX + index * stepX
        : width / 2;

      const yValue = scaleY(item.value);
      const yLower = scaleY(item.lower);
      const yUpper = scaleY(item.upper);

      // Ensure y1 is the lower Y coordinate (visually bottom) and y2 is upper (visually top)
      // Since SVG Y is inverted, smaller values map to larger Y coordinates
      // So if lower < upper in data, then yLower > yUpper in SVG coordinates
      // For line drawing, we need y1 (top of line in SVG) = yUpper, y2 (bottom of line) = yLower
      bars.push({ x, y1: Math.min(yLower, yUpper), y2: Math.max(yLower, yUpper) });
      dots.push({
        x,
        y: yValue,
        date: item.date,
        value: item.value,
        lower: item.lower,
        upper: item.upper
      });
      linePoints.push(`${x},${yValue}`);
    });

    this.chartPaths = {
      bars,
      dots,
      line: linePoints.join(' ')
    };

    console.log('[MarketValueCard] chartPaths calculated:', this.chartPaths);
    console.log('[MarketValueCard] Triggering change detection');
    this.cdr.markForCheck();
  }
}
